import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'

export interface SearchOptions<T> {
  searchKeys?: (keyof T)[]
  debounce?: number
  filterFn?: (item: T, query: string) => boolean
  caseSensitive?: boolean
  fuzzy?: boolean
  fuzzyThreshold?: number
  minQueryLength?: number
  exactMatch?: boolean
}

interface UseSearchFieldReturn<T> {
  filteredData: T[]
  loading: boolean
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
  query: string
  clearSearch: () => void
  resultCount: number
}

function fuzzyScore(target: string, query: string): number {
  if (!query) return 1
  if (!target) return 0

  const q = query.toLowerCase()
  const t = target.toLowerCase()

  if (t === q) return 1

  let queryIndex = 0
  let consecutiveMatches = 0
  let maxConsecutive = 0
  let totalMatches = 0

  for (let i = 0; i < t.length && queryIndex < q.length; i++) {
    if (t[i] === q[queryIndex]) {
      consecutiveMatches++
      maxConsecutive = Math.max(maxConsecutive, consecutiveMatches)
      totalMatches++
      queryIndex++
    } else {
      consecutiveMatches = 0
    }
  }

  if (queryIndex < q.length) return 0

  const matchRatio = totalMatches / t.length
  const consecutiveBonus = maxConsecutive / q.length
  const positionBonus = t.indexOf(q) === 0 ? 0.2 : 0

  return Math.min(1, matchRatio * 0.4 + consecutiveBonus * 0.4 + positionBonus)
}

function isRecord(value: unknown): value is Record<string | number | symbol, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function safeToString(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number') return value.toString()
  if (typeof value === 'boolean') return value.toString()
  if (Array.isArray(value)) return value.join(' ')
  if (isRecord(value)) {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

export function useSearchField<T>(
  data: T[],
  options: SearchOptions<T> = {}
): UseSearchFieldReturn<T> {
  const {
    searchKeys,
    debounce = 300,
    filterFn,
    caseSensitive = false,
    fuzzy = false,
    fuzzyThreshold = 0.5,
    minQueryLength = 0,
    exactMatch = false,
  } = options

  const [query, setQuery] = useState('')
  const deferredQuery = useDeferredValue(query)
  const [loading, setLoading] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const onSearch = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setQuery(value)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      setLoading(true)
      timeoutRef.current = setTimeout(() => {
        setLoading(false)
      }, debounce)
    },
    [debounce]
  )

  const clearSearch = useCallback(() => {
    setQuery('')
    setLoading(false)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  const filteredData = useMemo(() => {
    if (!deferredQuery || deferredQuery.length < minQueryLength) {
      return data
    }

    if (!Array.isArray(data) || data.length === 0) {
      return []
    }

    const normalizedQuery = caseSensitive ? deferredQuery : deferredQuery.toLowerCase()

    return data.filter((item) => {
      if (filterFn) {
        return filterFn(item, normalizedQuery)
      }

      if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
        const itemStr = safeToString(item)
        const searchStr = caseSensitive ? itemStr : itemStr.toLowerCase()

        if (fuzzy) {
          return fuzzyScore(searchStr, normalizedQuery) >= fuzzyThreshold
        }

        if (exactMatch) {
          return searchStr === normalizedQuery
        }

        return searchStr.includes(normalizedQuery)
      }

      if (isRecord(item) && searchKeys && searchKeys.length > 0) {
        return searchKeys.some((key) => {
          const value = item[key]
          const valueStr = safeToString(value)
          const searchStr = caseSensitive ? valueStr : valueStr.toLowerCase()

          if (fuzzy) {
            return fuzzyScore(searchStr, normalizedQuery) >= fuzzyThreshold
          }

          if (exactMatch) {
            return searchStr === normalizedQuery
          }

          return searchStr.includes(normalizedQuery)
        })
      }

      if (isRecord(item) && (!searchKeys || searchKeys.length === 0)) {
        const searchableValues = Object.values(item)
          .map(safeToString)
          .filter((str) => str.length > 0)

        return searchableValues.some((valueStr) => {
          const searchStr = caseSensitive ? valueStr : valueStr.toLowerCase()

          if (fuzzy) {
            return fuzzyScore(searchStr, normalizedQuery) >= fuzzyThreshold
          }

          if (exactMatch) {
            return searchStr === normalizedQuery
          }

          return searchStr.includes(normalizedQuery)
        })
      }

      return false
    })
  }, [
    deferredQuery,
    data,
    searchKeys,
    filterFn,
    caseSensitive,
    fuzzy,
    fuzzyThreshold,
    minQueryLength,
    exactMatch,
  ])

  const resultCount = filteredData.length

  return {
    filteredData,
    loading,
    onSearch,
    query,
    clearSearch,
    resultCount,
  }
}
